import React, { useEffect, useMemo } from 'react';

import { MapTo, Utils } from '@adobe/aem-react-editable-components';
import { TwoColumnsLayout } from 'Layouts/TwoColumnLayout/TwoColumnLayout';
import { ReactComponent as ArrowIcon } from 'assets/pics/arrowDownIcon.svg';
import { ReactComponent as DocumentIcon } from 'assets/pics/DocumentIcon.svg';
import { useAuth } from 'context/Auth';
import { useFormik } from 'formik';
import { useCurrentExperts } from 'hooks/UseCurrentExperts';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';
import { useSendAntragMessage, useTaskDetail } from 'hooks/UseMyTasks';
import { useFormUtilityData } from 'hooks/useFormUtilityData';
import { useSearchParam } from 'react-use';
import * as Yup from 'yup';

import { YDebFormSchema } from 'components/DEBForm/ValidationStructure/YDebFormSchema';
import {
  ADDRESS_SUBMIT_FIELD_KEYS,
  COMPANY_FIELDS,
} from 'components/FormHelper/AddressInput/addressInput.constants';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { TaskDetails } from 'components/MyTaskArea/TaskDetailPage/TaskDetails';
import {
  combineHouseNumberAndAddition,
  getAddressEntityIds,
  getTaskDetailAddressFieldSchema,
  getTaskDetailFieldSchema,
  isAddressMessageField,
} from 'components/MyTaskArea/TaskDetailPage/taskDetailAddressUtils';
import { Entity, getEntityLabel } from 'components/MyTaskArea/taskDetailConstants';
import {
  buildTaskDetailFileUploadContext,
  collectTaskDetailFilesToUpload,
} from 'components/MyTaskArea/utils/taskDetailFormUtils';
import { getDocumentUploadFieldName } from 'components/MyTaskArea/utils/mapTaskDetail';
import { RenderAemComponent } from 'components/RenderAemComponent/RenderAemComponent';

import { VALIDATION_CONSTANTS } from 'global/constants';

import { formatDate, toISODateString } from 'utils/DatesUtils';
import { getCamelCaseFieldName, getCanonicalFieldName, getFieldType } from 'utils/FieldNameMapping';
import { getTenantId } from 'utils/tenantUtil';

import './TaskDetailPage.sass';

const ADDRESS_VALIDATION_KEYS = Object.values(COMPANY_FIELDS);

const TaskDetailPageEditConfig = {
  emptyLabel: 'Task Detail Page',
  isEmpty(props) {
    return !props;
  },
};

const EMAIL_FIELD_KEYS = new Set(['email', 'netzportalEmail']);

const getExpertDisplayName = (expert) => {
  const name = [expert?.vorname, expert?.nachname].filter(Boolean).join(' ').trim();
  return name ? `${getEntityLabel(Entity.SPECIALIST)} ${name}` : getEntityLabel(Entity.SPECIALIST);
};

const getSubmitFieldName = (field) => {
  const canonical = getCanonicalFieldName(field.field);
  if (EMAIL_FIELD_KEYS.has(canonical)) {
    // EIV accepts camelCase expert field names (same as getCamelCaseFieldName).
    return Number(field.entity) === Entity.SPECIALIST ? 'netzportalEmail' : 'email';
  }
  return getCamelCaseFieldName(field.field);
};

const createValidationSchema = (taskDetail) => {
  if (!taskDetail?.lastMessage) {
    return Yup.object().shape({
      changes: Yup.string().max(1000, VALIDATION_CONSTANTS.MAX_LENGTH),
    });
  }

  const debSchema = YDebFormSchema(null, false);
  const addressEntityIds = getAddressEntityIds(taskDetail.lastMessage.fields);

  const validationObject = {
    changes: Yup.string().max(1000, VALIDATION_CONSTANTS.MAX_LENGTH),
  };

  (taskDetail.lastMessage.files ?? []).forEach((fileEntry) => {
    validationObject[getDocumentUploadFieldName(fileEntry.id)] = Yup.array()
      .min(1, VALIDATION_CONSTANTS.MANDATORY)
      .required(VALIDATION_CONSTANTS.MANDATORY);
  });

  (taskDetail.lastMessage.fields ?? []).forEach((field) => {
    const canonicalKey = getCanonicalFieldName(field.field);
    const key = `${field.entityId}_${canonicalKey}`;
    validationObject[key] = getTaskDetailFieldSchema(
      canonicalKey,
      getFieldType(canonicalKey),
      debSchema
    );
  });

  addressEntityIds.forEach((entityId) => {
    ADDRESS_VALIDATION_KEYS.forEach((canonicalKey) => {
      const key = `${entityId}_${canonicalKey}`;
      validationObject[key] = getTaskDetailAddressFieldSchema(canonicalKey, debSchema, entityId);
    });
  });

  return Yup.object().shape(validationObject);
};

const EXCLUDED_SUBMIT_FIELDS = new Set([
  'bBerufsbezeichnungId',
  'bFachrichtungId',
  'bZusatzId',
  'eintragungsartStrom',
  'eintragungsartGas',
]);

const createInitialValues = (taskDetail) => {
  const initialValues = { changes: '' };

  if (!taskDetail?.lastMessage) {
    return initialValues;
  }

  (taskDetail.lastMessage.files ?? []).forEach((fileEntry) => {
    initialValues[getDocumentUploadFieldName(fileEntry.id)] = [];
  });

  (taskDetail.lastMessage.fields ?? []).forEach((field) => {
    const canonicalKey = getCanonicalFieldName(field.field);
    initialValues[`${field.entityId}_${canonicalKey}`] = field.value ?? '';
  });

  getAddressEntityIds(taskDetail.lastMessage.fields).forEach((entityId) => {
    ADDRESS_VALIDATION_KEYS.forEach((canonicalKey) => {
      const key = `${entityId}_${canonicalKey}`;
      if (!(key in initialValues)) {
        initialValues[key] = '';
      }
    });
  });

  return initialValues;
};

export const TaskDetailPage = (props) => {
  const taskId = useSearchParam('taskId');
  const isEditor = Utils.isInEditor();
  const { token } = useAuth();
  const { installer } = useCurrentInstaller({ skip: isEditor });
  // Include experts with null names (rejected/unsaved contact data still has an id).
  const { experts } = useCurrentExperts({ skip: isEditor, requireName: false });
  const { comboBoxOptions } = useFormUtilityData();

  const { taskDetail, isLoading } = useTaskDetail({
    taskId: Number(taskId),
    skip: isEditor,
  });

  const entityNameMap = useMemo(() => {
    const map = {};
    if (installer?.id) {
      map[`${Entity.INSTALLER}_${installer.id}`] =
        installer.firmenname1 || getEntityLabel(Entity.INSTALLER);
    }
    (experts ?? []).forEach((e) => {
      map[`${Entity.SPECIALIST}_${e.id}`] = getExpertDisplayName(e);
    });

    // Fallback for entities referenced by the task but missing from installer/experts data.
    const messageFields = [
      ...(taskDetail?.lastMessage?.fields ?? []),
      ...(taskDetail?.messages ?? []).flatMap((message) => message.fields ?? []),
    ];
    messageFields.forEach((field) => {
      const key = `${field.entity}_${field.entityId}`;
      if (!map[key]) {
        map[key] = field.entityLabel || getEntityLabel(field.entity);
      }
    });

    return map;
  }, [installer, experts, taskDetail]);

  const { submitAntragMessage, isSubmitting, submitError, submitSuccess } = useSendAntragMessage(
    Number(taskId)
  );

  useEffect(() => {
    if (submitSuccess) window.location.reload();
  }, [submitSuccess]);
  const formik = useFormik({
    initialValues: createInitialValues(taskDetail),
    validationSchema: createValidationSchema(taskDetail),
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      const lastMessage = taskDetail?.lastMessage;
      const addressEntityIds = getAddressEntityIds(lastMessage?.fields);

      const mapFieldValue = (field) => {
        const raw = String(
          values[`${field.entityId}_${getCanonicalFieldName(field.field)}`] ?? field.value ?? ''
        );
        const canonical = getCanonicalFieldName(field.field);
        if (canonical === 'hwkBetriebsleiter' || canonical === 'isExpertOwner') {
          if (raw === 'true') return '1';
          if (raw === 'false') return '0';
          return '';
        }
        return raw;
      };

      const nonAddressFields = (lastMessage?.fields ?? [])
        .filter((field) => {
          const canonical = getCanonicalFieldName(field.field);
          if (EXCLUDED_SUBMIT_FIELDS.has(canonical)) {
            return false;
          }
          if (isAddressMessageField(field) && addressEntityIds.has(field.entityId)) {
            return false;
          }
          return true;
        })
        .map((field) => ({
          entity: field.entity,
          entityId: field.entityId,
          field: getSubmitFieldName(field),
          value: toISODateString(mapFieldValue(field)),
        }))
        .filter((f) => f.value !== '');

      const addressFields = [...addressEntityIds].flatMap((entityId) => {
        const referenceField = (lastMessage?.fields ?? []).find(
          (field) => field.entityId === entityId && isAddressMessageField(field)
        );
        if (!referenceField) {
          return [];
        }

        return ADDRESS_SUBMIT_FIELD_KEYS.filter(
          (canonicalKey) => canonicalKey !== COMPANY_FIELDS.HOUSE_NUMBER_ADDITION
        )
          .map((canonicalKey) => {
            let value = String(values[`${entityId}_${canonicalKey}`] ?? '');
            if (canonicalKey === COMPANY_FIELDS.HOUSE_NUMBER) {
              value = combineHouseNumberAndAddition(
                value,
                values[`${entityId}_${COMPANY_FIELDS.HOUSE_NUMBER_ADDITION}`]
              );
            }
            return {
              entity: referenceField.entity,
              entityId,
              field: getCamelCaseFieldName(canonicalKey),
              value: toISODateString(value),
            };
          })
          .filter((f) => f.value !== '');
      });

      const fields = [...nonAddressFields, ...addressFields];

      const fileUploadContext = buildTaskDetailFileUploadContext({
        installer,
        experts,
        lastMessageFields: lastMessage?.fields,
      });
      const filesToUpload = collectTaskDetailFilesToUpload(
        lastMessage?.files ?? [],
        values,
        fileUploadContext
      );

      await submitAntragMessage({
        message: values.changes ?? '',
        fields,
        filesToUpload,
      });
    },
  });

  if (isEditor) {
    return <RenderAemComponent componentProps={props} name="TaskDetailPage" />;
  }

  if (isLoading || !taskDetail) {
    return <LoadingIndicator />;
  }

  const attachments = taskDetail.lastMessage?.attachments ?? [];

  const backendBase = (process.env.REACT_APP_GRAPHQL_ENDPOINT ?? '').replace(
    /\/graphql(\?.*)?$/,
    ''
  );

  const getAttachmentDownloadUrl = (downloadUrl) => {
    if (!downloadUrl) {
      return null;
    }
    const separator = downloadUrl.includes('?') ? '&' : '?';
    return `${backendBase}${downloadUrl}${separator}tenant=${getTenantId()}`;
  };

  const handleAttachmentDownload = async (e, attachment) => {
    e.preventDefault();
    const url = getAttachmentDownloadUrl(attachment.downloadUrl);
    if (!url) return;
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        console.error('Failed to download attachment:', response.status);
        return;
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = attachment.displayName;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Error downloading attachment:', err);
    }
  };

  const scrollToComments = () => {
    const commentsElement = document.getElementById('task-comments');
    if (!commentsElement) {
      return;
    }
    window.scrollTo({
      top: commentsElement.offsetTop - 100,
      behavior: 'smooth',
    });
  };

  const rightColumn = (
    <div className="task-detail-sidebar">
      <div className="task-info">
        <div>Eingangsdatum: {formatDate(taskDetail.createdAt)}</div>
        <div>
          Status: <span className="task-info__status">{taskDetail.status}</span>
        </div>
        {attachments.length > 0 && (
          <>
            <div className="task-info__attachments-title">Anhang:</div>
            <div className="task-info__attachments">
              {attachments
                .filter((attachment) => attachment.downloadUrl)
                .map((attachment) => (
                  <a
                    key={attachment.id}
                    href={attachment.downloadUrl}
                    onClick={(e) => handleAttachmentDownload(e, attachment)}
                    className="task-info__attachments__item">
                    <DocumentIcon />
                    {attachment.displayName}
                  </a>
                ))}
            </div>
          </>
        )}
      </div>
      {(taskDetail.messages?.length ?? 0) > 0 && (
        <div className="task-details__jump-to-comments">
          <button
            type="button"
            className="task-details__jump-to-comments__trigger"
            onClick={scrollToComments}>
            Zum Nachrichtenverlauf springen
          </button>{' '}
          <ArrowIcon className="arrow-icon" onClick={scrollToComments} />
        </div>
      )}
    </div>
  );

  return (
    <>
      <TwoColumnsLayout
        noRightMargin
        leftColumn={
          <TaskDetails
            taskData={taskDetail}
            formik={formik}
            onDownloadAttachment={handleAttachmentDownload}
            isSubmitting={isSubmitting}
            submitError={submitError}
            submitSuccess={submitSuccess}
            entityNameMap={entityNameMap}
            comboBoxOptions={comboBoxOptions}
          />
        }
        rightColumn={rightColumn}
      />
      {isSubmitting && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.6)',
            zIndex: 9999,
          }}>
          <LoadingIndicator />
        </div>
      )}
    </>
  );
};

MapTo('e-fix/components/content/taskdetailpage')(TaskDetailPage, TaskDetailPageEditConfig);
